import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { BenefitsEligibilitySummary, formatStediDate, Stedi271 } from './eligibilityData';

// The "Overview" block of a completed eligibility result: the subscriber's identity/plan details
// and the payer, both read from the raw Stedi 271. Plan dates come from the decoded summary
// (coverageDates) and render in red when coverage is inactive.

interface Props {
  stedi: Stedi271;
  summary?: BenefitsEligibilitySummary;
  inactive: boolean;
  // The selected payer's logo (from the payer-search candidate — the 271 itself carries no logo).
  payerLogo?: string | null;
}

const GENDER_LABEL: Record<string, string> = { m: 'Male', male: 'Male', f: 'Female', female: 'Female' };

export default function EligibilityOverview({ stedi, summary, inactive, payerLogo }: Props) {
  const sub = stedi.subscriber ?? {};
  const addr = sub.address ?? {};
  const payer = stedi.payer ?? {};
  const plan = stedi.planInformation ?? {};

  const planDates = summary?.coverageDates
    ? `${formatStediDate(summary.coverageDates.start)} – ${formatStediDate(summary.coverageDates.end)}`
    : '';

  // Only render fields the payer actually returned — a 271 varies a lot by payer, and a wall of
  // em dashes reads as broken. Plan Dates always shows (it carries the active/inactive signal).
  const subscriberFields: { label: string; value?: string; danger?: boolean; always?: boolean }[] = [
    { label: 'First Name', value: sub.firstName },
    { label: 'Middle Name', value: sub.middleName },
    { label: 'Last Name', value: sub.lastName },
    { label: 'Date of Birth', value: sub.dateOfBirth ? formatStediDate(sub.dateOfBirth) : undefined },
    { label: 'Sex', value: sub.gender ? (GENDER_LABEL[sub.gender.toLowerCase()] ?? sub.gender) : undefined },
    { label: 'Address', value: [addr.address1, addr.address2].filter(Boolean).join(', ') || undefined },
    { label: 'City', value: addr.city },
    { label: 'State', value: addr.state },
    { label: 'Zip', value: addr.postalCode },
    { label: 'Member ID', value: sub.memberId },
    { label: 'Plan', value: plan.planDescription || sub.groupDescription },
    { label: 'Plan Number', value: sub.planNumber || plan.planNumber },
    { label: 'Group Number', value: sub.groupNumber },
    { label: 'Plan Dates', value: planDates, danger: inactive, always: true },
  ];
  const shownFields = subscriberFields.filter((f) => f.always || (f.value && f.value.length > 0));

  const payerId = payer.payorIdentification || payer.payerIdentification;
  const payerFields = [
    { label: 'Name', value: payer.name },
    { label: 'Payer ID', value: payerId },
  ].filter((f) => f.value && f.value.length > 0);

  return (
    <Section data-testid='eligibility-overview'>
      <SectionHeader>Overview</SectionHeader>

      <Block>
        <BlockTitle>Subscriber</BlockTitle>
        <Fields>
          {shownFields.map((field) => (
            <Field key={field.label}>
              <FieldLabel>{field.label}</FieldLabel>
              <FieldValue $danger={field.danger}>{field.value || '—'}</FieldValue>
            </Field>
          ))}
        </Fields>
      </Block>

      {payerFields.length > 0 && (
        <Block>
          <BlockTitle>Payer</BlockTitle>
          <PayerRow>
            <Logo aria-hidden data-testid='payer-logo'>
              {payerLogo ? <img src={payerLogo} alt='' /> : <LogoFallback />}
            </Logo>
            <Fields>
              {payerFields.map((field) => (
                <Field key={field.label}>
                  <FieldLabel>{field.label}</FieldLabel>
                  <FieldValue>{field.value}</FieldValue>
                </Field>
              ))}
            </Fields>
          </PayerRow>
        </Block>
      )}
    </Section>
  );
}

const Section = styled.div`
  border: 1px solid ${colors.black15};
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled(BodySmallBold)`
  display: block;
  padding: 16px 24px;
  font-size: 15px;
  background: ${colors.primaryBlue05};
  border-bottom: 1px solid ${colors.black10};
  color: ${colors.black};
`;

const Block = styled.div`
  padding: 20px 24px;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

const BlockTitle = styled(BodySmallBold)`
  display: block;
  margin-bottom: 16px;
  color: ${colors.black};
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px 40px;
`;

const PayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Logo = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const LogoFallback = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: ${colors.black05};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled(BodySmall)`
  color: ${colors.black50};
`;

const FieldValue = styled(BodySmall)<{ $danger?: boolean }>`
  color: ${({ $danger }) => ($danger ? colors.accentRed : colors.black)};
  font-weight: ${({ $danger }) => ($danger ? 700 : 400)};
`;
